import React from 'react'
import styled from 'styled'

import { CarouselItem, CarouselItemValue } from 'generated/mos/org/getty/audio'

import { Container, Content, ItemIndex, Wrap, DragHandle } from 'components/ui/draggable'
import { IconPadlock } from 'components/ui/icons'
import { Image, Subtitle, Description, Title } from 'components/ui/list-item-label'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'

import { audioCarouselItemValueSchema, videoCarouselItemValueSchema, imageCarouselItemValueSchema } from 'domains/org/getty/schemas/carousel-item'

import { areObjectsEqual } from 'helpers/core'
import { space } from 'helpers/style'

import { Media } from 'generated/mos/media'

const HeroImageMessage = styled.span`
  color: ${({ theme }) => theme.color.gray};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme }) => theme.font.weight.base};
  line-height: 1.6;
  margin-right: ${space(4)};
  white-space: nowrap;
`
const HeroImageSection = styled.div`
  display: flex;
`

const HeroImageIndexSection = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  margin-left: ${space(4)};
`;

type Props = {
  readonly carouselItem?: CarouselItem.Entity;
  readonly deleteHandler?: (carouselItem: CarouselItem.Ref) => void;
  readonly draggableIndex?: number;
  readonly historyPush: (id: string) => void;
  readonly isDragging: boolean;
  readonly isHeroImage?: boolean;
  readonly mediaList: ReadonlyArray<Media.Entity> | undefined;
  readonly projectId: string;
  readonly stopId: string;
  readonly stopImageRef?: Media.Ref;
  readonly tourId?: string;
  readonly hostName: string;
}

type CarouselItemValueItem = CarouselItemValue.AudioItem.Entity | CarouselItemValue.ImageItem.Entity | CarouselItemValue.VideoItem.Entity | CarouselItemValue.HeroImageItem.Entity

export const MediaCarouselItem = ({
  draggableIndex,
  carouselItem,
  deleteHandler,
  historyPush,
  hostName,
  isDragging,
  isHeroImage,
  mediaList,
  projectId,
  stopId,
  stopImageRef,
  tourId,
}: Props) => {

  const carouselItemValue = carouselItem && carouselItem.value && carouselItem.value && carouselItem.value.item;
  const carouselItemRef = carouselItem && carouselItem.ref

  const heroImageId = stopImageRef && stopImageRef.id
  const heroImagePath = heroImageId && hostName ? `${hostName}/images/${heroImageId}.jpg?s=crop&h=92&w=92` : ''

  // where to navigate to after the carousel item is successfully created
  // consider whether we got here from within a tour or not
  const redirectLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}` : `/project/${projectId}/stop/${stopId}`

  // title shown on the list item should default to "-" if there's no media attached to the carousel item
  const resolveItemTitle = (mediaRef: Media.Ref | undefined) => {
    if (!mediaList || !mediaRef) {
      return '-'
    }

    const media = mediaList.find((media) => media.ref && areObjectsEqual(media.ref, mediaRef))

    return media && media.title ? media.title : '-'
  }

  // get the title and type fo carousel item
  const getItemDetails = (itemValue: CarouselItemValueItem) => {
    const itemDetails = {
      itemTitle: "",
      itemType: "",
      itemImagePath: ""
    }

    switch(itemValue.type) {
      case 'mos.org.getty.audio.CarouselItemValue.AudioItem': {
        return {
          ...itemDetails,
          itemTitle: resolveItemTitle(audioCarouselItemValueSchema.audio.read(itemValue.audio) as any as Media.Ref),
          itemType: 'Audio'
        }
      }
      case 'mos.org.getty.audio.CarouselItemValue.VideoItem': {
        return {
          ...itemDetails,
          itemTitle: resolveItemTitle(videoCarouselItemValueSchema.video.read(itemValue.video) as any as Media.Ref),
          itemType: 'Video'
        }
      }
      case 'mos.org.getty.audio.CarouselItemValue.ImageItem': {
        const imageRef = imageCarouselItemValueSchema.fullScreenImageRef.read(itemValue.fullScreenImageRef) as any as Media.Ref
        const imageId = imageRef && imageRef.id
        const imagePath = imageId && hostName ? `${hostName}/images/${imageId}.jpg?s=crop&h=92&w=92` : ''

        return {
          ...itemDetails,
          itemTitle: resolveItemTitle(imageRef),
          itemType: 'Image',
          itemImagePath: imagePath
        }
      }
      default: {
        return itemDetails
      }
    }
  }

  return (
    <>
      <Container isDragging={isDragging}>
        <Wrap isDragging={isDragging} isDragDisabled={!!isHeroImage}>
          {draggableIndex !== undefined && <DragHandle />}
          <Content>
            {!!isHeroImage &&
              <>
                <HeroImageIndexSection />
                <ItemIndex>
                  1
                </ItemIndex>
              </>
            }
            {draggableIndex &&
              <ItemIndex>
                {draggableIndex + 1 }
              </ItemIndex>
            }
            {carouselItemValue &&
              <>
                <Image src={getItemDetails(carouselItemValue).itemImagePath} />
                <Description>
                  <Title>{getItemDetails(carouselItemValue).itemTitle}</Title>
                  <Subtitle>{getItemDetails(carouselItemValue).itemType}</Subtitle>
                </Description>
              </>
            }
            {!!isHeroImage &&
              <>
                <Image src={heroImagePath} />
                <Description>
                  <Title>{resolveItemTitle(stopImageRef)}</Title>
                  <Subtitle>Image</Subtitle>
                </Description>
              </>
            }
          </Content>
          {!!isHeroImage &&
            <HeroImageSection>
              <HeroImageMessage>
                First slide is derived from the "Stop Hero Image"
              </HeroImageMessage>
              <IconPadlock size="base" />
            </HeroImageSection>
          }
          {carouselItemRef && deleteHandler &&
            <PopOverMenu iconSize="base">
              <PopOverMenuButton
                onClick={() => {
                  const carouselItemId = CarouselItem.mustRef(carouselItemRef).id
                  historyPush(`${redirectLocation}/media-carousel/${carouselItemId}`)
                }}
              >
              Edit
              </PopOverMenuButton>
              <PopOverMenuButton
                onClick={() => deleteHandler(carouselItemRef)}
                >
                  Delete
              </PopOverMenuButton>
            </PopOverMenu>
        }
        </Wrap>
      </Container>
    </>
  )
}
